/* eslint-disable react-refresh/only-export-components -- co-localizamos
   el helper puro `computeProgress` con su único consumidor. HMR no
   crítico; los tests se benefician del export del helper. */
/**
 * Barra de progreso visual del checklist de un expediente
 * (Sprint 11 · 5).
 *
 * Componente puro derivado: cuenta items completados / total y pinta
 * un porcentaje con color según el avance.
 *
 * Sin checklist (= 0 items) no se renderiza nada — evitamos mostrar
 * "100%" engañoso cuando el expediente todavía no tiene template.
 *
 * Color por umbral (semáforo):
 * - <33%  rojo (proyecto recién arrancó).
 * - <80%  ámbar (en curso).
 * - ≥80%  verde (casi listo).
 *
 * Los umbrales son arbitrarios pero coherentes con cómo el funcionario
 * percibe el avance.
 */
import type { ChecklistItemDTO } from "@/lib/api";

export interface ProjectProgressBarProps {
  checklist: ChecklistItemDTO[];
}

export function computeProgress(checklist: ChecklistItemDTO[]): {
  total: number;
  completed: number;
  pct: number;
  color: "red" | "amber" | "emerald";
} | null {
  const total = checklist.length;
  if (total === 0) return null;
  const completed = checklist.filter((i) => i.is_completed).length;
  const pct = Math.round((completed / total) * 100);
  let color: "red" | "amber" | "emerald" = "red";
  if (pct >= 80) color = "emerald";
  else if (pct >= 33) color = "amber";
  return { total, completed, pct, color };
}

const COLOR_CLASS: Record<"red" | "amber" | "emerald", string> = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
};

export function ProjectProgressBar({ checklist }: ProjectProgressBarProps) {
  const progress = computeProgress(checklist);
  if (progress === null) return null;

  return (
    <div className="mt-3" data-testid="project-progress">
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-semibold tracking-wide uppercase">Progreso</span>
        <span>
          {progress.completed} de {progress.total} items · {progress.pct}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={"h-full transition-all " + COLOR_CLASS[progress.color]}
          style={{ width: `${progress.pct}%` }}
          aria-label={`Progreso ${progress.pct}%`}
          role="progressbar"
          aria-valuenow={progress.pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}
